import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaImage, FaVideo, FaUpload, FaCalendar, FaEye, FaDownload, FaShare, FaChartBar } from 'react-icons/fa';
import { wishesService } from '../../services/wishesService';
// Using Tailwind CSS classes instead of external CSS

const WishesManagement = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    occasionName: '',
    dates: [''],
    category: 'Festival',
    tags: '',
    status: 'active'
  });
  const [uploading, setUploading] = useState(false);

  const categories = ['Festival', 'Birthday', 'Anniversary', 'Holiday', 'Other'];

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const response = await wishesService.getAllWishesMedia();

      // Helper function to build proper URLs
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const buildUrl = (url) => {
        if (!url) return null;
        // If URL already starts with http/https, return as-is (Cloudinary URLs)
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        }
        // Otherwise prepend baseURL for local server URLs
        return `${baseURL}${url}`;
      };

      const mediaWithUrls = response.data.map(item => ({
        ...item,
        mediaUrl: buildUrl(item.mediaUrl),
        thumbnailUrl: buildUrl(item.thumbnailUrl)
      }));

      setMedia(mediaWithUrls);
    } catch (error) {
      console.error('Error fetching media:', error);
      alert('Error loading media. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        alert('Please upload only image or video files');
        return;
      }

      // Validate file size
      const maxSize = isImage ? 10 * 1024 * 1024 : 50 * 1024 * 1024; // 10MB for images, 50MB for videos
      if (file.size > maxSize) {
        alert(`File size exceeds ${isImage ? '10MB' : '50MB'} limit`);
        return;
      }

      setUploadData({ ...uploadData, file });
    }
  };

  const handleDateChange = (index, value) => {
    const newDates = [...uploadData.dates];
    newDates[index] = value;
    setUploadData({ ...uploadData, dates: newDates });
  };

  const handleAddDate = () => {
    setUploadData({ ...uploadData, dates: [...uploadData.dates, ''] });
  };

  const handleRemoveDate = (index) => {
    const newDates = uploadData.dates.filter((_, i) => i !== index);
    setUploadData({ ...uploadData, dates: newDates.length > 0 ? newDates : [''] });
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadData.file) {
      alert('Please select a file to upload');
      return;
    }

    if (!uploadData.occasionName) {
      alert('Please enter occasion name');
      return;
    }

    const validDates = uploadData.dates.filter(date => date !== '');
    if (validDates.length === 0) {
      alert('Please add at least one date');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('media', uploadData.file);
      formData.append('occasionName', uploadData.occasionName);
      formData.append('dates', JSON.stringify(validDates));
      formData.append('category', uploadData.category);
      if (uploadData.tags) {
        formData.append('tags', JSON.stringify(uploadData.tags.split(',').map(tag => tag.trim())));
      }
      formData.append('status', uploadData.status);

      await wishesService.uploadWishesMedia(formData);

      alert('Media uploaded successfully!');
      setShowUploadModal(false);
      setUploadData({
        file: null,
        occasionName: '',
        dates: [''],
        category: 'Festival',
        tags: '',
        status: 'active'
      });
      fetchMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      alert('Error uploading media. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this media?')) {
      return;
    }

    try {
      await wishesService.deleteWishesMedia(id);
      alert('Media deleted successfully!');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Error deleting media. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate stats
  const totalEvents = media.length;
  const totalImages = media.filter(item => item.mediaType === 'image').length;
  const totalVideos = media.filter(item => item.mediaType === 'video').length;
  const totalDownloads = media.reduce((acc, item) => acc + (item.downloadCount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Events & Wishes Management</h1>
          <p className="text-muted-foreground">Create date-specific media content for personalized greetings</p>
        </div>
        <button
          className="btn btn-default"
          onClick={() => setShowUploadModal(true)}
        >
          <FaPlus className="mr-2 h-4 w-4" />
          Create New Event
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Total Events</div>
            <FaCalendar className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalEvents}</div>
            <p className="text-xs text-muted-foreground">+5% from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Images</div>
            <FaImage className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalImages}</div>
            <p className="text-xs text-muted-foreground">+12% from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Videos</div>
            <FaVideo className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalVideos}</div>
            <p className="text-xs text-muted-foreground">+8% from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Downloads</div>
            <FaDownload className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalDownloads}</div>
            <p className="text-xs text-muted-foreground">+18% from last month</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-content">
            <div className="flex flex-col items-center justify-center min-h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-sm text-muted-foreground">Loading events...</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="card-title">All Events</div>
              <select className="select w-48">
                <option>All Categories</option>
                <option>Festival</option>
                <option>Birthday</option>
                <option>Anniversary</option>
                <option>Holiday</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div className="card-content">

          {media.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FaCalendar className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No events yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Create your first event to get started with personalized content</p>
              <button
                className="btn btn-default mt-6"
                onClick={() => setShowUploadModal(true)}
              >
                <FaPlus className="mr-2 h-4 w-4" />
                <span>Create Your First Event</span>
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {media.map(item => (
                <div key={item._id} className="card hover:shadow-md transition-shadow">
                  <div className="card-header">
                    <div className="flex items-center justify-between">
                      <span className="badge badge-secondary">{item.category}</span>
                      <button
                        className="btn btn-ghost btn-icon text-destructive hover:text-destructive"
                        onClick={() => handleDelete(item._id)}
                        title="Delete Event"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    </div>
                  </div>

                  <div className="relative overflow-hidden p-4">
                    <div className="absolute top-2 left-2 z-10">
                      <span className="badge badge-default flex items-center">
                        {item.mediaType === 'image' ? <FaImage className="mr-1 h-3 w-3" /> : <FaVideo className="mr-1 h-3 w-3" />}
                        {item.mediaType}
                      </span>
                    </div>
                    {item.mediaType === 'image' ? (
                      <img 
                        src={item.mediaUrl} 
                        alt={item.occasionName}
                        className="aspect-video w-full object-cover"
                      />
                    ) : (
                      <div className="relative aspect-video">
                        <img 
                          src={item.thumbnailUrl || item.mediaUrl} 
                          alt={item.occasionName}
                          className="aspect-video w-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/80 text-primary-foreground">
                            <FaVideo className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="card-content">
                    <h3 className="font-semibold leading-none tracking-tight mb-3">{item.occasionName}</h3>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <FaCalendar className="mr-2 h-3 w-3" />
                        <span>
                          {item.dates.length > 1 ? 
                            `${item.dates.length} dates` : 
                            formatDate(item.dates[0])
                          }
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className={`badge ${item.isActive ? 'badge-default' : 'badge-secondary'} text-xs`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {item.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="badge badge-outline text-xs">{tag}</span>
                        ))}
                        {item.tags.length > 3 && (
                          <span className="badge badge-outline text-xs text-muted-foreground">+{item.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="card-footer">
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <FaDownload className="mr-1 h-3 w-3" />
                          <span>{item.downloadCount || 0}</span>
                        </div>
                        <div className="flex items-center">
                          <FaShare className="mr-1 h-3 w-3" />
                          <span>{item.shareCount || 0}</span>
                        </div>
                      </div>
                      
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => console.log('View event:', item._id)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold">Create New Event</h3>
                <p className="text-sm text-muted-foreground">Upload media for specific dates and occasions</p>
              </div>
              <button 
                className="btn btn-ghost btn-icon"
                onClick={() => setShowUploadModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="label">Media File *</label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                  <input
                    type="file"
                    id="file-input"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="sr-only"
                  />
                  <label htmlFor="file-input" className="flex flex-col items-center cursor-pointer">
                    <FaUpload className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm font-medium">
                      {uploadData.file ? uploadData.file.name : 'Choose image or video file'}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1">
                      Images: max 10MB | Videos: max 50MB
                    </span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className="label">Occasion Name *</label>
                <input
                  type="text"
                  className="input"
                  value={uploadData.occasionName}
                  onChange={(e) => setUploadData({ ...uploadData, occasionName: e.target.value })}
                  placeholder="e.g., Diwali, Christmas, Birthday"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="label">Category *</label>
                <select
                  className="select"
                  value={uploadData.category}
                  onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="label">Dates *</label>
                <div className="space-y-2">
                  {uploadData.dates.map((date, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="date"
                        className="input flex-1"
                        value={date}
                        onChange={(e) => handleDateChange(index, e.target.value)}
                        required
                      />
                      {uploadData.dates.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon text-destructive hover:text-destructive"
                          onClick={() => handleRemoveDate(index)}
                        >
                          <FaTrash className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" className="btn btn-outline btn-sm mt-2" onClick={handleAddDate}>
                  <FaPlus className="mr-1 h-3 w-3" /> Add Another Date
                </button>
              </div>

              <div className="space-y-2">
                <label className="label">Tags (comma-separated)</label>
                <input
                  type="text"
                  className="input"
                  value={uploadData.tags}
                  onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                  placeholder="e.g., festival, lights, celebration"
                />
              </div>

              <div className="space-y-2">
                <label className="label">Status</label>
                <select
                  className="select"
                  value={uploadData.status}
                  onChange={(e) => setUploadData({ ...uploadData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-border">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-default" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload Media'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WishesManagement;
